'use es6';

import I18n from 'I18n';
import { findIndex, findWhere, isEqual, isNull, isUndefined, omit } from 'underscore';
import updateItemInArray from 'forms-utils-lib/utils/array/updateItemInArray';
import addItemToArray from 'forms-utils-lib/utils/array/addItemToArray';
import { FormErrors, parseFormError } from 'FormsUI/api/Forms';
import { getFormWithDraft } from 'FormsUI/api/FormDrafts';
import { getDefinition, getLastFetched, getFormFieldGroups, getFormDefinitionValidation, getFormLanguage, getFormMetaData, getFormDefinitionRequestState, getPaymentFieldFromFormDefinition, getPublishedPaymentField } from 'FormsUI/selectors/FormDefinition';
import { getMainFormFieldGroups } from 'FormsUI/selectors/FormFieldsProperties';
import { getWindowLocationSearch, getWindowLocationPathname } from 'FormsUI/utils/WindowGetters';
import { getPaymentsToggleState, getIsPaymentLinkOnForm } from 'FormsUI/selectors/Payments';
import { isUngatedForNewEditorAccess } from 'FormsUI/selectors/Gates';
import createNewFormFieldGroup from 'forms-utils-lib/formFieldGroup/utils/createFormFieldGroup';
import { Validation, ValidationKeys, ValidationType, TabValidationsKeys } from 'FormsUI/constants/FormFieldValidations';
import PublishState from 'FormsUI/constants/PublishState';
import FormTypes from 'forms-utils-lib/constants/formDefinition/FormTypes';
import { UnknownProperties } from 'forms-utils-lib/constants/Properties';
import EmbedVersions from 'forms-utils-lib/constants/formDefinition/EmbedVersions';
import { scrollMainPanel } from 'FormsUI/utils/AnimationUtils';
import { getFormsLangToLang } from 'FormsUI/utils/Lang';
import { isFieldAPaymentField } from 'FormsUI/utils/FormFieldsUtils';
import updateFormLanguage from 'FormsUI/actions/FormDefinition/updateFormLanguage';
import { update as updateFormFieldAction } from 'FormsUI/actions/Field';
import { addNewFormFieldGroup, deleteFormField } from 'FormsUI/actions/FormDragDrop';
import { addPaymentsValidationError } from 'FormsUI/actions/Payments';
import { generateFormUrl } from 'FormUrlGenerator';
import { navigateTo } from 'FormsUI/utils/UrlUtils';
import Log from 'forms-utils-lib/logger/Logger';
import { getDashboardRoute } from 'FormsCommonComponents/lib/RouteGenerators';
import LocalSettings from 'FormsCommonComponents/utils/LocalSettings';
import { fetchCurrentInbox } from 'FormsUI/actions/Inboxes';
import { isRichTextInternalIdEqualFieldGroupInternalId } from 'FormsUI/utils/RichText';
import { updatePrivateInternalIdsOnFieldGroups } from 'FormsUI/utils/FormFieldsUtils';
import redirectToNewEditor from 'FormsUI/actions/formCreation/newEditor/redirectToNewEditor';
import updateFormFieldKey from 'forms-utils-lib/formField/utils/updateFormFieldKey';
import hydrateFormWithDefaults from 'FormsUI/utils/formDefinition/hydrateFormWithDefaults';
export function createAction(name) {
  return `@@formDefinition/${name}`;
}
export const REQUEST_FORM_DEFINITION = createAction('REQUEST_FORM_DEFINITION');
export const SUCCEED_GETTING_FORM_DEFINITION = createAction('SUCCEED_GETTING_FORM_DEFINITION');
export const FAIL_GETTING_FORM_DEFINITION = createAction('FAIL_GETTING_FORM_DEFINITION');
export const RECEIVE_FORM_DEFINITION = createAction('RECEIVE_FORM_DEFINITION');
export const UPDATE_FORM_VALUE = createAction('UPDATE_FORM_VALUE');
export const UPDATE_FORM_VALUE_SIMPLE = createAction('UPDATE_FORM_VALUE_SIMPLE');
export const UPDATE_POST_SUBMISSION_OPTION = createAction('UPDATE_POST_SUBMISSION_OPTION');
export const UPDATE_FORM_FIELD = createAction('UPDATE_FORM_FIELD');
export const UPDATE_FORM_FIELD_GROUP = createAction('UPDATE_FORM_FIELD_GROUP');
export const ADD_VALIDATION_ERROR = createAction('ADD_VALIDATION_ERROR');
export const REMOVE_VALIDATION_ERROR = createAction('REMOVE_VALIDATION_ERROR');
export const TOGGLE_STYLE_SIDE_PANEL = createAction('TOGGLE_STYLE_SIDE_PANEL');
export const RESET_STYLE_PANEL = createAction('RESET_STYLE_PANEL');
export const START_UPDATE_FORM_LANGUAGE = createAction('START_UPDATE_FORM_LANGUAGE');
export const END_UPDATE_FORM_LANGUAGE = createAction('END_UPDATE_FORM_LANGUAGE');
export const UPDATE_SELECTED_EXTERNAL_OPTIONS = createAction('UPDATE_SELECTED_EXTERNAL_OPTIONS');
export const RESET_FORM_DEFINITION = createAction('RESET_FORM_DEFINITION');
const MAX_DATA_AGE = 300000; // 5 minutes in millis

export function requestFormDefinition() {
  return {
    type: REQUEST_FORM_DEFINITION
  };
}
export function receiveFormDefinition(definition, formPublishState, publishedForm) {
  return {
    type: RECEIVE_FORM_DEFINITION,
    definition,
    formPublishState,
    publishedForm,
    meta: {
      timestamp: Date.now()
    }
  };
}
export function succeedGettingFormDefinition() {
  return {
    type: SUCCEED_GETTING_FORM_DEFINITION
  };
}
function failGettingFormDefinition(reason = FormErrors.UNKNOWN) {
  return {
    type: FAIL_GETTING_FORM_DEFINITION,
    reason
  };
}
export function updateFormValue(payload) {
  return {
    type: UPDATE_FORM_VALUE,
    payload
  };
}
export function simpleUpdateFormValue(updatedValue) {
  return {
    type: UPDATE_FORM_VALUE_SIMPLE,
    updatedValue
  };
}
function updatePostSubmissionOption(updatedOption) {
  return {
    type: UPDATE_POST_SUBMISSION_OPTION,
    updatedOption
  };
}
export function updateFormField(formFieldGroups) {
  return {
    type: UPDATE_FORM_FIELD,
    formFieldGroups
  };
}
function updateFormFieldGroupValue(formFieldGroups) {
  return {
    type: UPDATE_FORM_FIELD_GROUP,
    formFieldGroups
  };
}
function addValidationError(payload) {
  return {
    type: ADD_VALIDATION_ERROR,
    payload
  };
}
function removeValidationError(payload) {
  return {
    type: REMOVE_VALIDATION_ERROR,
    payload
  };
}
export const toggleStyleSidePanel = section => ({
  type: TOGGLE_STYLE_SIDE_PANEL,
  section
});
export const resetStyleSidePanel = () => ({
  type: RESET_STYLE_PANEL
});
export const resetFormDefinition = () => ({
  type: RESET_FORM_DEFINITION
});
function _shouldRedirectForm(form) {
  if (LocalSettings.get('DISABLE_EDITOR_REDIRECT_ON_FORM_TYPE')) {
    return false;
  }
  return form.formType === FormTypes.FLOW;
}
function _shouldRedirectV4Form(form) {
  return form && form.embedVersion === EmbedVersions.V4;
}
function _redirectNonHubSpotForm(form) {
  const formInfoForUrl = {
    portalId: form.portalId,
    formType: form.formType,
    guid: form.guid,
    editor: getWindowLocationPathname().indexOf('editor') > -1,
    tab: getWindowLocationPathname().indexOf('followUp') > -1 ? 'followUp' : null
  };
  if (form.formType === FormTypes.VIDEO_FORM) {
    navigateTo(getDashboardRoute());
    return;
  }
  const queryParams = new URLSearchParams(getWindowLocationSearch()).toString();
  const url = generateFormUrl(formInfoForUrl);
  if (!queryParams) {
    navigateTo(url);
    return;
  }
  const hasSearch = url.indexOf('?') > -1;
  const urlWithSearch = `${url}${hasSearch ? '&' : '?'}${queryParams}`;
  navigateTo(urlWithSearch);
}
export function addValidationErrorIfNeeded(step, field, validationKey, options) {
  return (dispatch, getState) => {
    const validations = getFormDefinitionValidation(getState()) || [];
    const fieldKey = field.substring(field.lastIndexOf('.') + 1);
    const newValidations = Object.assign({}, validations, {
      [step]: Object.assign({}, validations[step], {
        [field]: Object.assign({}, validations[step][field], {
          [validationKey]: Object.assign({}, (validations[step][field] || {})[validationKey], Object.assign({
            key: Validation[fieldKey][validationKey]
          }, options))
        })
      })
    });
    dispatch(addValidationError(newValidations));
  };
}
export function addFieldValidationErrorIfNeeded(fieldName, fieldObjectTypeId, field, validationKey, options) {
  return addValidationErrorIfNeeded(TabValidationsKeys.FORM, `fields.${fieldObjectTypeId}.${fieldName}.${field}`, validationKey, options);
}
export function removeValidationErrorIfNeeded(step, field) {
  return (dispatch, getState) => {
    const validation = getFormDefinitionValidation(getState()) || [];
    if (validation && validation[step] && Object.prototype.hasOwnProperty.call(validation[step], field)) {
      const newValidations = Object.assign({}, validation, {
        [step]: omit(validation[step], field)
      });
      return dispatch(removeValidationError(newValidations));
    }
    return null;
  };
}
export function removeLegalConsentErrors() {
  return dispatch => {
    dispatch(removeValidationErrorIfNeeded(TabValidationsKeys.FORM, ValidationKeys.LEGAL_CONSENT, ValidationType.INVALID));
  };
}
export function removeFieldValidationErrorIfNeeded(fieldName, fieldObjectTypeId, field, validationKey, options) {
  return removeValidationErrorIfNeeded('form', `fields.${fieldObjectTypeId}.${fieldName}.${field}`, validationKey, options);
}
export const fetchFormDefinitionSuccess = (response, options) => (dispatch, getState) => {
  const ungatedForNewEditor = isUngatedForNewEditorAccess(getState());
  if (ungatedForNewEditor && options.redirect && _shouldRedirectV4Form(response.form)) {
    redirectToNewEditor(response.form.guid, 'firstLaunch');
    return null;
  }
  if (options.redirect && _shouldRedirectForm(response.form)) {
    _redirectNonHubSpotForm(response.form);
    return null;
  }
  const definition = response.publishState === PublishState.PUBLISHED ? response.form : response.formDraft;
  dispatch(succeedGettingFormDefinition());
  const formDefinitionWithDefaults = hydrateFormWithDefaults(definition);
  dispatch(receiveFormDefinition(formDefinitionWithDefaults, response.publishState, response.form));
  if (!formDefinitionWithDefaults.formFieldGroups.length) {
    dispatch(addValidationErrorIfNeeded(TabValidationsKeys.FORM, ValidationKeys.NO_FIELDS, ValidationType.REQUIRED));
  }
  const state = getState();
  const shouldDispatchPaymentValidationError = getPaymentsToggleState(state) && !getIsPaymentLinkOnForm(state);
  if (shouldDispatchPaymentValidationError) {
    dispatch(addPaymentsValidationError());
  }
  dispatch(fetchCurrentInbox());
  return response;
};
export const fetchFormDefinitionError = err => dispatch => {
  const {
    xhr = {}
  } = err;
  const {
    status
  } = xhr;
  switch (status) {
    case 404:
      dispatch(failGettingFormDefinition(FormErrors.NOT_FOUND));
      break;
    default:
      dispatch(failGettingFormDefinition(parseFormError(xhr)));
      if (status !== 403) Log.logServiceError('Unknown error trying to fetch form', xhr);
  }
  return err;
};
export const fetchFormDefinition = (formGuid, customOptions = {}) => dispatch => {
  const options = Object.assign({
    redirect: true
  }, customOptions);
  dispatch(requestFormDefinition());
  return getFormWithDraft(formGuid).then(response => {
    dispatch(fetchFormDefinitionSuccess(response, options));
    return response;
  }).catch(err => {
    dispatch(fetchFormDefinitionError(err));
    return err;
  });
};
export const fetchFormDefinitionIfNeeded = (formGuid, options = {}) => (dispatch, getState) => {
  const formDefinition = getDefinition(getState());
  const requestState = getFormDefinitionRequestState(getState());
  const lastFetched = getLastFetched(getState());
  const isPending = requestState.isPending();
  const isOutdated = Date.now() - lastFetched > MAX_DATA_AGE;
  const isDifferent = formDefinition.guid !== formGuid;
  const canBeFetched = !formDefinition || isDifferent || !lastFetched || isOutdated;
  if (!isPending && canBeFetched) {
    return dispatch(fetchFormDefinition(formGuid, options));
  }
  return Promise.resolve();
};
export function updateFormFieldDefinition(fieldName, objectTypeId, attribute, value) {
  return (dispatch, getState) => {
    const formFieldGroups = getFormFieldGroups(getState());
    const [formFieldGroupIndex, formFieldIndex] = formFieldGroups.reduce((accum, group, index) => {
      if (accum[1] === -1) {
        const fieldIndex = findIndex(group.fields, {
          objectTypeId,
          name: fieldName
        });
        return [index, fieldIndex];
      }
      return accum;
    }, [-1, -1]);
    if (formFieldIndex > -1) {
      const oldFormFieldGroup = formFieldGroups[formFieldGroupIndex];
      const oldFormField = formFieldGroups[formFieldGroupIndex].fields[formFieldIndex];
      const updatedFormField = updateFormFieldKey(oldFormField, attribute, value);
      const updatedFormFieldGroup = Object.assign({}, oldFormFieldGroup, {
        fields: updateItemInArray(oldFormFieldGroup.fields, formFieldIndex, updatedFormField)
      });
      const updatedFormFieldGroups = updateItemInArray(formFieldGroups, formFieldGroupIndex, updatedFormFieldGroup);
      dispatch(updateFormField(updatedFormFieldGroups));
    }
  };
}
export function updateFormFieldValidationDefinition(fieldName, formFieldPropertyObjectType, attribute, value) {
  return (dispatch, getState) => {
    const formFieldGroups = getFormFieldGroups(getState());
    let formFieldGroupIndex = -1;
    let formFieldIndex = -1;
    while (isEqual(formFieldIndex, -1) && formFieldGroupIndex < formFieldGroups.length - 1) {
      formFieldGroupIndex = formFieldGroupIndex + 1;
      formFieldIndex = findIndex(formFieldGroups[formFieldGroupIndex].fields, {
        name: fieldName,
        propertyObjectType: formFieldPropertyObjectType
      });
    }
    if (formFieldIndex > -1) {
      const oldFormFieldGroup = formFieldGroups[formFieldGroupIndex];
      const oldFormField = formFieldGroups[formFieldGroupIndex].fields[formFieldIndex];
      const updatedFormFieldValidation = Object.assign({}, oldFormField.validation, {
        [attribute]: value
      });
      const updatedFormField = Object.assign({}, oldFormField, {
        validation: updatedFormFieldValidation
      });
      const updatedFormFieldGroup = Object.assign({}, oldFormFieldGroup, {
        fields: updateItemInArray(oldFormFieldGroup.fields, formFieldIndex, updatedFormField)
      });
      const updatedFormFieldGroups = updateItemInArray(formFieldGroups, formFieldGroupIndex, updatedFormFieldGroup);
      dispatch(updateFormField(updatedFormFieldGroups));
    }
  };
}
export function updateRichText(richTextPrivateInternalId, newRichText) {
  return (dispatch, getState) => {
    const formFieldGroups = getFormFieldGroups(getState());
    const formFieldGroupIndex = formFieldGroups.findIndex(formFieldGroup => isRichTextInternalIdEqualFieldGroupInternalId(formFieldGroup, richTextPrivateInternalId));
    if (formFieldGroupIndex !== -1) {
      const oldFormFieldGroup = formFieldGroups[formFieldGroupIndex];
      const updatedFormFieldGroup = Object.assign({}, oldFormFieldGroup, newRichText);
      const updatedFormFieldGroups = updateItemInArray(formFieldGroups, formFieldGroupIndex, updatedFormFieldGroup);
      dispatch(updateFormField(updatedFormFieldGroups));
    }
  };
}
export function updateValueOnFormDefinition(updatedValue, {
  scrollTarget = false
} = {}) {
  return (dispatch, getState) => {
    const {
      value: updatedFormLang
    } = updatedValue.metaData && findWhere(updatedValue.metaData, {
      name: 'lang'
    }) || {};
    const currentFormLang = getFormLanguage(getState());
    if (updatedFormLang && !isEqual(updatedFormLang, currentFormLang)) {
      dispatch(updateFormLanguage(currentFormLang, updatedFormLang));
    }
    dispatch(updateFormValue(updatedValue));
    if (scrollTarget) {
      scrollMainPanel(scrollTarget);
    }
  };
}
export function removeMetaData(name) {
  return (dispatch, getState) => {
    const currentMetaData = getFormMetaData(getState());
    const updatedMetaData = currentMetaData.filter(metaData => !isEqual(metaData.name, name));
    return dispatch(updateValueOnFormDefinition({
      metaData: updatedMetaData
    }));
  };
}
export function updateMetaDataValue(name, value) {
  return (dispatch, getState) => {
    const currentMetaData = getFormMetaData(getState());
    const updatedMetaData = [...currentMetaData.filter(item => item.name !== name), {
      name,
      value
    }];
    dispatch(updateValueOnFormDefinition({
      metaData: updatedMetaData
    }));
  };
}
export function updateMetaData(name, value) {
  return dispatch => {
    if (isNull(value) || isUndefined(value)) {
      dispatch(removeMetaData(name));
    } else {
      dispatch(updateMetaDataValue(name, value));
    }
  };
}
export function updateFormFieldGroup(formFieldGroupIndex, updatedValue) {
  return (dispatch, getState) => {
    const formFieldGroups = getFormFieldGroups(getState());
    const formFieldGroup = formFieldGroups[formFieldGroupIndex];
    const newFormFieldGroup = Object.assign({}, formFieldGroup, updatedValue);
    const updatedFormFieldGroups = updateItemInArray(formFieldGroups, formFieldGroupIndex, newFormFieldGroup);
    dispatch(updateFormFieldGroupValue(updatedFormFieldGroups));
  };
}
export function updatePostFormSubmissionOption(updatedOption) {
  return dispatch => {
    dispatch(updatePostSubmissionOption(updatedOption));
  };
}
export function updateDefaultStyling(updatedValue) {
  return dispatch => {
    dispatch(simpleUpdateFormValue(updatedValue));
  };
}
export const revertUpdatePaymentField = paymentField => (dispatch, getState) => {
  const state = getState();
  const draftPaymentField = getPaymentFieldFromFormDefinition(state);
  if (!paymentField) {
    const formFieldGroups = getFormFieldGroups(state);
    const mappedFormFieldGroups = formFieldGroups.map(fieldGroup => Object.assign({}, fieldGroup, {
      fields: fieldGroup.fields.filter(field => !isFieldAPaymentField(field))
    }));
    const filteredAndMappedFormFieldGroups = mappedFormFieldGroups.filter(fieldGroup => fieldGroup.fields.length || fieldGroup.richText.type && fieldGroup.richText.content);
    dispatch(deleteFormField(filteredAndMappedFormFieldGroups, UnknownProperties.PAYMENT_LINK));
    if (!filteredAndMappedFormFieldGroups.length) {
      dispatch(addValidationErrorIfNeeded(TabValidationsKeys.FORM, ValidationKeys.NO_FIELDS, ValidationType.REQUIRED));
    }
  } else if (draftPaymentField) {
    dispatch(updateFormFieldAction(paymentField));
  } else {
    const formFieldGroups = getMainFormFieldGroups(state);
    const newFormFieldGroup = createNewFormFieldGroup({
      field: paymentField
    });
    if (newFormFieldGroup) {
      const updatedFormFieldGroups = updatePrivateInternalIdsOnFieldGroups(addItemToArray(formFieldGroups, formFieldGroups.length, newFormFieldGroup));
      dispatch(addNewFormFieldGroup(updatedFormFieldGroups));
    }
  }
  const formLanguage = getFormLanguage(state);
  const hasPaymentFieldOnPublishedForm = !!paymentField;
  if (hasPaymentFieldOnPublishedForm && !draftPaymentField) {
    dispatch(updateFormValue({
      submitText: I18n.text('defaultForm.continueToPayment', {
        locale: getFormsLangToLang(formLanguage)
      })
    }));
  } else if (!hasPaymentFieldOnPublishedForm && !!draftPaymentField) {
    dispatch(updateFormValue({
      submitText: I18n.text('defaultForm.submitLabel', {
        locale: getFormsLangToLang(formLanguage)
      })
    }));
  }
};
export const revertPaymentChanges = () => (dispatch, getState) => {
  const state = getState();
  const publishedPaymentField = getPublishedPaymentField(state);
  return dispatch(revertUpdatePaymentField(publishedPaymentField));
};